export const PERMANENT_WEBSOCKET_CLOSE_CODES = new Set([
  4004, // Session not found (backend sends this)
  1008, // Policy violation
  4000, // Legacy session-expired close code
  4001, // Custom: Unauthorized
  4003, // Custom: Forbidden
  1000, // Custom: Clean closure
]);

const RESUME_DEBUG_CHUNK_TYPES = new Set([
  "START",
  "DONE",
  "RESET",
  "TUTOR_ACTION",
]);

const CANVAS_SUPPRESSED_TUTOR_CHUNK_TYPES = new Set([
  "START",
  "DONE",
  "AUDIO",
  "CAPTIONS_UPDATE",
  "TUTOR_ACTION",
]);

type ChunkData = string | Record<string, unknown> | null | undefined;

export interface WsChunk {
  chunk_type: string;
  chunk_data?: unknown;
  turn_id?: string | null;
  timestamp?: unknown;
}

export interface AudioWorkletChunk {
  buffer: ArrayBufferLike;
}

export function shouldRetryWebSocketClose(code: number): boolean {
  return !PERMANENT_WEBSOCKET_CLOSE_CODES.has(code);
}

export function getWebSocketRetryDelayMs(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 30000);
}

export function shouldRecordInboundResumeDebug(chunkType: string): boolean {
  return RESUME_DEBUG_CHUNK_TYPES.has(chunkType);
}

export function shouldIgnoreInboundChunkForTurn(
  chunkTurnId: unknown,
  currentTurnId: unknown,
): boolean {
  return chunkTurnId === null || chunkTurnId !== currentTurnId;
}

export function shouldSuppressTutorInboundChunk({
  chunkType,
  isNotepadOpen,
  isNudgePending,
}: {
  chunkType: string;
  isNotepadOpen: boolean;
  isNudgePending: boolean;
}): boolean {
  return (
    isNotepadOpen &&
    !isNudgePending &&
    CANVAS_SUPPRESSED_TUTOR_CHUNK_TYPES.has(chunkType)
  );
}

export function shouldIgnoreExtraChunkAfterDone({
  doneChunkReceived,
  chunkType,
}: {
  doneChunkReceived: boolean;
  chunkType: string;
}): boolean {
  // DISCUSS_CONTEXT_FAILED is a control signal enqueued before the new turn's
  // START, so it always lands in the post-DONE window of the previous turn.
  return (
    doneChunkReceived &&
    chunkType !== "START" &&
    chunkType !== "TUTOR_ACTION" &&
    chunkType !== "DISCUSS_CONTEXT_FAILED"
  );
}

export function buildTurnChangeChunk({
  turnId,
  timestamp,
  isInterrupt,
  captureProfile,
  tutorAudioOn,
  hasFailedSlideChange,
}: {
  turnId: string;
  timestamp: unknown;
  isInterrupt: boolean;
  captureProfile?: Record<string, unknown> | null;
  tutorAudioOn?: boolean;
  hasFailedSlideChange?: boolean;
}): WsChunk {
  return {
    chunk_type: "TURN_CHANGE",
    turn_id: turnId,
    timestamp,
    chunk_data: JSON.stringify({
      is_interrupt: isInterrupt,
      ...(captureProfile ? { capture_profile: captureProfile } : {}),
      ...(tutorAudioOn === undefined ? {} : { tutor_audio_on: tutorAudioOn }),
      ...(hasFailedSlideChange ? { has_failed_slide_change: true } : {}),
    }),
  };
}

export function encodeAudioChunkForWs(chunkData: AudioWorkletChunk): string {
  return btoa(String.fromCharCode(...new Uint8Array(chunkData.buffer)));
}

export function buildAudioChunk({
  chunkData,
  timestamp,
}: {
  chunkData: AudioWorkletChunk;
  timestamp: unknown;
}): WsChunk {
  return {
    chunk_type: "AUDIO",
    chunk_data: encodeAudioChunkForWs(chunkData),
    timestamp,
  };
}

export function buildDoneChunk({
  turnId,
  timestamp,
}: {
  turnId: string | null;
  timestamp: unknown;
}): WsChunk {
  return {
    chunk_type: "DONE",
    turn_id: turnId,
    chunk_data: "",
    timestamp,
  };
}

export function buildTextChunk({
  userMessage,
  model,
  timestamp,
  source,
  inputMode,
  tutorAudioOn,
}: {
  userMessage: unknown;
  model: unknown;
  timestamp: unknown;
  source?: unknown;
  inputMode?: unknown;
  tutorAudioOn?: unknown;
}): WsChunk {
  return {
    chunk_type: "TEXT",
    chunk_data: JSON.stringify({
      user_message: userMessage,
      model,
      source,
      input_mode: inputMode,
      tutor_audio_on: tutorAudioOn,
    }),
    timestamp,
  };
}

export function buildDataChunk({
  chunkType,
  chunkData,
  timestamp,
}: {
  chunkType: string;
  chunkData?: ChunkData;
  timestamp: unknown;
}): WsChunk {
  const chunk: WsChunk = {
    chunk_type: chunkType,
    timestamp,
  };

  if (chunkData !== undefined) {
    chunk.chunk_data = chunkData;
  }

  return chunk;
}

function parseChunkData(chunkData: unknown): unknown {
  return typeof chunkData === "string" ? JSON.parse(chunkData) : chunkData;
}

export function parseTutorActionDebugPayload(chunkData: unknown): {
  action_time: unknown;
  fragment_ids: unknown;
  action: unknown;
} {
  const parsedData = parseChunkData(chunkData) as Record<
    string,
    unknown
  > | null;
  const rawTutorAction = parsedData?.tutor_action;
  const tutorAction =
    typeof rawTutorAction === "string"
      ? JSON.parse(rawTutorAction)
      : rawTutorAction;
  const tutorActionRecord = tutorAction as Record<string, unknown> | null;

  return {
    action_time: parsedData?.action_time,
    fragment_ids: tutorActionRecord?.fragment_ids,
    action: tutorActionRecord?.action,
  };
}

export function parseUploadedImagesStatus(chunkData: unknown): {
  failedImages: { index: number; reason: "quality" | "error" }[];
} {
  const parsed = parseChunkData(chunkData) as Record<string, unknown> | null;
  const failedImages = Array.isArray(parsed?.failed_images)
    ? (parsed.failed_images as { index: number; reason: "quality" | "error" }[])
    : [];

  return { failedImages };
}

/**
 * The submission sources the client knows how to re-arm. The backend names sources in
 * DISCUSS_CONTEXT_FAILED using these exact strings; both sides assert this set in their
 * tests, so a rename on either side fails loudly instead of silently releasing nothing.
 */
export const RELEASABLE_SUBMISSION_SOURCES = {
  INPUTS: "inputs",
  SCRATCHPAD: "scratchpad",
} as const;

/**
 * Total parse of a chunk_data that addresses one submission.
 *
 * Every message in this exchange is addressed by submit_id, and none of them may throw:
 * a malformed payload has to degrade to "unaddressed" rather than take the handler down
 * with it. parseChunkData throws on broken JSON, and older backends send an empty string.
 */
function parseSubmitId(chunkData: unknown): {
  submitId: string | null;
  isMalformed: boolean;
  parsed: Record<string, unknown> | null;
} {
  let parsed: Record<string, unknown> | null = null;
  let isMalformed = false;
  // An empty payload is the normal shape, not a broken one: every turn nobody
  // submitted for sends chunk_data="", and so does an older backend. Parsing it
  // would throw and flag every voice turn as malformed.
  if (chunkData === "" || chunkData == null) {
    return { submitId: null, isMalformed: false, parsed: null };
  }
  try {
    parsed = parseChunkData(chunkData) as Record<string, unknown> | null;
  } catch {
    // Reported rather than swallowed: a broken payload degrades to the same
    // "unaddressed" shape a genuine voice turn produces, and the two want very
    // different reactions from whoever is reading the log.
    parsed = null;
    isMalformed = true;
  }
  const rawSubmitId = parsed?.submit_id;
  return {
    submitId:
      typeof rawSubmitId === "string" && rawSubmitId ? rawSubmitId : null,
    isMalformed,
    parsed,
  };
}

export function parseTurnStart(chunkData: unknown): {
  submitId: string | null;
  isMalformed: boolean;
} {
  // A START that names a submission acknowledges that one and no other. Turns nobody
  // submitted for — voice, nudges, the intro — carry no id, and neither do older
  // backends.
  const { submitId, isMalformed } = parseSubmitId(chunkData);
  return { submitId, isMalformed };
}

export function parseDiscussContextFailed(chunkData: unknown): {
  submitId: string | null;
  released: string[];
  ignoredSources: string[];
  isMalformed: boolean;
} {
  const { submitId, isMalformed, parsed } = parseSubmitId(chunkData);
  const rawReleased = parsed?.released;
  const names = Array.isArray(rawReleased)
    ? rawReleased.filter((name): name is string => typeof name === "string")
    : [];
  const known: string[] = Object.values(RELEASABLE_SUBMISSION_SOURCES);

  // Names are split rather than merely filtered. A source this client cannot re-arm
  // must not look like an empty list: that is how a released source went unnoticed
  // for a whole release, and the next one added backend-first would too.
  return {
    submitId,
    isMalformed,
    released: names.filter((name) => known.includes(name)),
    ignoredSources: names.filter((name) => !known.includes(name)),
  };
}

export function parseTextDebugOutput(chunkData: unknown): {
  debugType: "stt_output" | "tts_output";
  source: string;
  text: string;
} | null {
  const parsedData = parseChunkData(chunkData) as Record<
    string,
    unknown
  > | null;
  if (
    parsedData?.debug_type !== "stt_output" &&
    parsedData?.debug_type !== "tts_output"
  ) {
    return null;
  }

  return {
    debugType: parsedData.debug_type,
    source:
      typeof parsedData.source === "string" ? parsedData.source : "unknown",
    text: typeof parsedData.text === "string" ? parsedData.text : "",
  };
}
